/**
 * Skirmish map sizes — pure sim-side configuration (no three.js, no DOM).
 *
 * A map size fixes the playable half-extent (world units), the resource
 * node budget, and the distance between the two starting bases. "standard"
 * preserves the exact pre-map-size tuning (half-extent 200, 12 timber / 8
 * stone nodes, bases ~147u apart) so existing balance and playtests are
 * unaffected; small/large scale content proportionally.
 *
 * The upcoming PCG landscape integration (feature/fog-of-war) should
 * consume MapSizeDef directly: halfExtent bounds terrain generation and
 * worldSize sizes the nav/vision grids.
 */
export type MapSizeId = "small" | "standard" | "large";

export interface MapSizeDef {
  id: MapSizeId;
  /** English label, e.g. "Small". */
  name: string;
  /** Latin subtitle for the Unreal-flavoured UI, e.g. "Parvus". */
  la: string;
  /** One-line description shown under the picker. */
  blurb: string;
  /** Playable half-extent in world units: the world spans -halfExtent..halfExtent. */
  halfExtent: number;
  /** Full world size (2 * halfExtent): sizes nav + vision grids. */
  worldSize: number;
  /** Timber / stone node counts scattered at game start. */
  nodeTimber: number;
  nodeStone: number;
  /** Distance between the player and enemy starting bases, world units. */
  baseDistance: number;
}

export const MAP_SIZES: Record<MapSizeId, MapSizeDef> = {
  small: {
    id: "small",
    name: "Small",
    la: "Parvus",
    blurb: "Tight valley. Fast contact — best on phones.",
    halfExtent: 120,
    worldSize: 240,
    nodeTimber: 8,
    nodeStone: 5,
    baseDistance: 88,
  },
  standard: {
    id: "standard",
    name: "Standard",
    la: "Ordinarius",
    blurb: "The classic Latium field. Balanced for desktop.",
    halfExtent: 200,
    worldSize: 400,
    nodeTimber: 12,
    nodeStone: 8,
    baseDistance: 147,
  },
  large: {
    id: "large",
    name: "Large",
    la: "Magnus",
    blurb: "Wide frontier. Room to manoeuvre, longer marches.",
    halfExtent: 300,
    worldSize: 600,
    nodeTimber: 16,
    nodeStone: 11,
    baseDistance: 221,
  },
};

export const MAP_SIZE_ORDER: MapSizeId[] = ["small", "standard", "large"];

export function isMapSizeId(v: unknown): v is MapSizeId {
  return v === "small" || v === "standard" || v === "large";
}

/**
 * Default map size for a fresh skirmish setup. Small on touch devices and
 * narrow screens (the phone default the playtesters asked for), Standard
 * everywhere else so desktop balance is untouched. The caller supplies the
 * DOM hints — this module stays DOM-free for sim testability.
 */
export function defaultMapSizeId(hints: {
  coarsePointer: boolean;
  narrowScreen: boolean;
}): MapSizeId {
  return hints.coarsePointer || hints.narrowScreen ? "small" : "standard";
}
